import { Component, OnInit, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { TranslateService } from '@ngx-translate/core';
import { PartnerAnalyticsService } from '@services/partner-analytic.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { formateDateTime, getCurrencySymbol } from '@utils/utils';
import * as moment from 'moment';
import { GridReadyEvent } from 'ag-grid-community';
import { LoaderService } from '@services/loader.service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-partner-report-referrals',
  templateUrl: './partner-report-referrals.component.html',
  styleUrls: ['./partner-report-referrals.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class PartnerListReportReferralsComponent implements OnInit {
  @ViewChild(AtlasGridComponent) grid: AtlasGridComponent;

  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };

  tableColumnDefs = null;
  tableGridOptions = null;
  paginationPageSize = 10;
  gridAnimationState = 'hide';

  constructor(
    private partnerAnalyticsService: PartnerAnalyticsService,
    private translateService: TranslateService,
    private loaderService: LoaderService
  ) {}

  ngOnInit(): void {
    const that = this;
    this.createTableReportsReferrals();
    this.tableGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationState = 'show';
      },
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      rowHeight: 115,
      loadFunction: this.reloadList.bind(that),
      filters: this.getFilters()
    });
  }
  private reloadList(startRow?, endRow?, sort?) {
    this.loaderService.show();
    return this.partnerAnalyticsService
      .getListReportReferrals(
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? this.getColId(sort[0].colId) : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null,
        this.getFilterMonth(),
        this.getFilterYear()
      )
      .pipe(finalize(() => this.loaderService.hide()));
  }

  private getColId(id: string) {
    switch (id) {
      case 'idUser':
        return 'ID_USER';
      case 'createDate':
        return 'CREATE_DATE';
      case 'createType':
        return 'LAST_MODIFY_DATE';
      case 'partnerProfit':
        return 'PROFIT';
      case 'totalAmount':
        return 'PRICE';
      case 'status':
        return 'STATE';
    }
  }

  private getFilterMonth(): string {
    const onlyMonth = this.grid.filtersModel.months;
    if (onlyMonth) {
      // const month = moment(onlyMonth).month() + 1;
      return onlyMonth.value.toString();
    }
    return null;
  }

  private getFilterYear(): string {
    const onlyYear = this.grid.filtersModel.years;
    if (onlyYear) {
      const year = moment(onlyYear).year();
      return year.toString();
    }
    return null;
  }

  getFilters() {
    return [
      {
        name: 'years',
        label: 'common_year',
        type: 'dateShowOnlyYears'
      },
      {
        name: 'months',
        label: 'common_month',
        type: 'dropdown',
        options: [
          {
            label: 'month_1',
            value: 1,
            translate: true
          },
          {
            label: 'month_2',
            value: 2,
            translate: true
          },
          {
            label: 'month_3',
            value: 3,
            translate: true
          },
          {
            label: 'month_4',
            value: 4,
            translate: true
          },
          {
            label: 'month_5',
            value: 5,
            translate: true
          },
          {
            label: 'month_6',
            value: 6,
            translate: true
          },
          {
            label: 'month_7',
            value: 7,
            translate: true
          },
          {
            label: 'month_8',
            value: 8,
            translate: true
          },
          {
            label: 'month_9',
            value: 9,
            translate: true
          },
          {
            label: 'month_10',
            value: 10,
            translate: true
          },
          {
            label: 'month_11',
            value: 11,
            translate: true
          },
          {
            label: 'month_12',
            value: 12,
            translate: true
          }
        ]
      }
    ];
  }

  private getTranslateStatus(value: string) {
    if (value) {
      let labels;
      const itemsLabel = 'partner_analytics_activitystatus_' + value.toLowerCase();
      this.translateService.get(itemsLabel).subscribe(label => {
        labels = label;
      });
      return labels;
    }
  }

  private getCurrencyItems(value: string) {
    return value + ' ' + getCurrencySymbol();
  }

  private createTableReportsReferrals() {
    this.tableColumnDefs = [
      {
        headerNameTranslate: 'partner_analytics_activitiesstable_order',
        field: 'idCart',
        sortable: false
      },
      {
        headerNameTranslate: 'partner_analytics_activitiesstable_date',
        field: 'createDate',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              return formateDateTime(params.data.createDate, false);
            }
            return '';
          }
        }
      },
      {
        headerNameTranslate: 'account_userid',
        field: 'idUser',
        sortable: false
      },
      {
        headerNameTranslate: 'partner_analytics_activitiesstable_grandtotal',
        field: 'totalAmount',
        sortable: false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => (params && params.data ? this.getCurrencyItems(params.data.totalAmount) : '')
        }
      },
      {
        headerNameTranslate: 'partner_analytics_activitiesstable_status',
        field: 'status',
        sortable: false,
        cellRenderer: type => {
          return this.getTranslateStatus(type.data?.status);
        }
      },
      {
        headerNameTranslate: 'partner_analytics_activitiesstable_earning',
        field: 'partnerProfit',
        cellRenderer: 'htmlRenderer',
        sortable: false,
        cellRendererParams: {
          content: params => (params && params.data ? this.getCurrencyItems(params.data.partnerProfit) : '')
        }
      }
    ];
  }
}
